"use client";

import { useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { CitationTool } from "@/components/citation-tool";
import type { InputMode } from "@/components/citation-tool/input-mode-tabs";
import { getFeaturedStyles } from "@/data/citation-styles";

interface IdentifierGeneratorProps {
  /** The query string that prefills the generator: `doi`, `isbn`, `pmid`, `url`… */
  param: string;
  /**
   * The tab the page opens on. Pages that start from a title or an author ("Search
   * Books", "Search Articles") open on that tab; the rest keep the auto-detect box.
   */
  initialMode?: InputMode;
  /** Example lines for the empty auto-detect box, in the kind of identifier the page is about. */
  autoPlaceholder?: string;
}

function IdentifierGeneratorInner({
  param,
  initialMode,
  autoPlaceholder,
}: IdentifierGeneratorProps) {
  const searchParams = useSearchParams();
  // Prefill the auto-detect generator from `?doi=` / `?isbn=` (or the shared `?source=`
  // deep link). The value runs through the same auto path as a manual paste, so a DOI or
  // ISBN resolves to a full reference on mount; `CitationTool` switches to the
  // auto-detect tab itself when it has a value to run.
  const prefill =
    searchParams.get(param) ?? searchParams.get("source") ?? undefined;

  return (
    <CitationTool
      autoPlaceholder={autoPlaceholder}
      initialMode={initialMode}
      source={prefill}
      styles={getFeaturedStyles()}
    />
  );
}

/**
 * Live citation generator embedded on the landing pages for one kind of source. It is
 * the same `<CitationTool>` the homepage uses; `param` names the query string that
 * prefills it, `initialMode` picks the tab the page starts on, and `autoPlaceholder`
 * shows examples of the identifier the page is about. `useSearchParams` requires a
 * Suspense boundary.
 */
export function IdentifierGenerator({
  param,
  initialMode,
  autoPlaceholder,
}: IdentifierGeneratorProps) {
  return (
    <Suspense
      fallback={
        <CitationTool
          autoPlaceholder={autoPlaceholder}
          initialMode={initialMode}
          styles={getFeaturedStyles()}
        />
      }
    >
      <IdentifierGeneratorInner
        autoPlaceholder={autoPlaceholder}
        initialMode={initialMode}
        param={param}
      />
    </Suspense>
  );
}
